import { useEffect, useCallback } from "react";
import { useMutation, useSubscription } from "@apollo/react-hooks";
import { MY_TIME_USERS_WITH_REQUEST } from "my-time/graphql";
import { MY_TIME_REQUESTS_SUBSCRIPTION } from "my-time/graphql/subscriptions";

type UseMyTimeUsersWithRequestsParams = {
  requestsOnly: boolean;
  showOvertime: boolean;
  showWorkingTimeChange: boolean;
  showStandbyCall: boolean;
  roleFilters: Record<string, any>;
  tableData?: {
    sortField?: string;
    sortDirection?: string;
    page?: number;
    pageSize?: number;
    searchField?: string;
    searchText?: string;
  };
  startDate?: string;
  endDate?: string;
  onCompleted: (data: any[]) => void;
};

type QueryParams = {
  requestsOnly: boolean;
  showOvertime: boolean;
  showWorkingTimeChange: boolean;
  showStandbyCall: boolean;
  roleFilters: Record<string, any>;
  sortField?: string;
  sortDirection?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  searchField?: string;
  searchText?: string;
};

export const useMyTimeUsersWithRequests = ({
  requestsOnly,
  showOvertime,
  showWorkingTimeChange,
  showStandbyCall,
  roleFilters,
  tableData,
  startDate,
  endDate,
  onCompleted,
}: UseMyTimeUsersWithRequestsParams) => {
  const { ...filters } = roleFilters;

  const periodStartDate = startDate ? new Date(startDate).getTime() : 0;
  const periodEndDate = endDate ? new Date(endDate).getTime() : 0;

  const queryParams = {
    requestsOnly,
    showOvertime,
    showWorkingTimeChange,
    showStandbyCall,
    roleFilters: filters,
    sortField: tableData?.sortField,
    sortDirection: tableData?.sortDirection,
    startDate,
    endDate,
    page: tableData?.page,
    pageSize: tableData?.pageSize,
    ...(tableData?.searchField && tableData?.searchText
      ? {
        searchField: tableData.searchField,
        searchText: tableData.searchText,
      }
      : {}),
  } as QueryParams;

  const [loadQuery, { data, loading, error }] = useMutation(
    MY_TIME_USERS_WITH_REQUEST,
    {
      variables: queryParams,
      onCompleted: (data) => {
        onCompleted(data?.myTimeUsersWithRequest || []);
      },
    }
  );

  useSubscription(MY_TIME_REQUESTS_SUBSCRIPTION, {
    fetchPolicy: "network-only",
    onData: () => {
      loadQuery();
    },
  });

  const memoLoadQuery = useCallback(() => {
    loadQuery();
  }, [loadQuery]);

  useEffect(() => {
    memoLoadQuery();
  }, [memoLoadQuery, periodStartDate, periodEndDate]);

  return {
    data: data?.myTimeUsersWithRequest || [],
    loading,
    error,
    usersCount: 0,
    allUserCount: 0,
    requestsCount: 0,
    loadQuery: memoLoadQuery,
  };
};

export default useMyTimeUsersWithRequests;
